import React, { useEffect, useMemo, useState } from 'react';
import { styled } from '../../../../theme/stitches.config';
// Components
import { SMPText, SMPFlex, SMPArrowUpIcon, SMPProgressBar } from '../../../index';

// Constants
import { ONE, X, TWO } from '../../../../constants/oddSigns';

// Hooks
import usePredictor from '../../../../hooks/usePredictor';

// Types
import {
  FansUnitedSDKOptions,
  OneXTwo,
  FansUnitedMarkets,
  LoadSmpData,
  ISubscriberFn,
  FansUnitedConfig,
  IFansUnitedDataLabels,
} from '../../../../types';
import { FansUnitedToggleTitle } from './FansUnitedToggleTitle';

export interface IFansUnitedFullTimeOutcomeProps {
  fansUnitedExpanded?: boolean;
  matchId?: string;
  homeTeam?: string;
  awayTeam?: string;
  fansUnitedMarket?: FansUnitedMarkets;
  fansUnitedIdSchema?: FansUnitedSDKOptions['idSchema'];
  fansUnitedApiKey?: FansUnitedSDKOptions['apiKey'];
  fansUnitedClientId?: FansUnitedSDKOptions['clientId'];
  fansUnitedEnvironment?: FansUnitedConfig['fansUnitedEnvironment'];
  fansUnitedGetAuthToken: LoadSmpData['getAuthToken'];
  fansUnitedSignOut: LoadSmpData['signOut'];
  subscribers: { [key: string]: ISubscriberFn[] };
  labels?: IFansUnitedDataLabels;
}

export const FansUnitedFullTimeOutcome = ({
  matchId,
  homeTeam,
  awayTeam,
  subscribers,
  fansUnitedApiKey,
  fansUnitedSignOut,
  fansUnitedIdSchema,
  fansUnitedClientId,
  fansUnitedEnvironment,
  fansUnitedGetAuthToken,
  fansUnitedMarket = 'FT_1X2',
  fansUnitedExpanded = false,
  labels,
}: IFansUnitedFullTimeOutcomeProps): JSX.Element => {
  const [isExpanded, setIsExpanded] = useState(fansUnitedExpanded);
  const [predictionSign, setPredictionSign] = useState<OneXTwo | null>(null);

  const handleCollapseToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const { summary, prediction, getMarketSummary, makePrediction } = usePredictor({
    matchId,
    subscribers,
    predictionSign,
    fansUnitedMarket,
    fansUnitedApiKey,
    fansUnitedSignOut,
    fansUnitedIdSchema,
    fansUnitedClientId,
    fansUnitedEnvironment,
    fansUnitedGetAuthToken,
  });

  useEffect(() => {
    if (prediction) {
      getMarketSummary();

      if (prediction?.fixtures[0].prediction.value !== predictionSign) {
        setPredictionSign(prediction?.fixtures[0].prediction.value);
      }
    }
  }, [prediction]);

  const handlePredictionClick = (value: OneXTwo) => {
    if (!prediction) {
      setPredictionSign(value);
      makePrediction({ matchId, fansUnitedMarket, predictionSign: value });
    }
  };

  const Wrapper = useMemo(
    () =>
      styled.withConfig({ componentId: 'FansUnitedWidget', displayName: 'FansUnitedFullTimeWrapper' })('div', {
        backgroundColor: '$fansUnitedContainerPrimaryBgColor',
      }),
    [],
  );

  return (
    <Wrapper>
      <SMPFlex variant='fansUnitedHeaderWrapper' onClick={handleCollapseToggle}>
        <SMPText size='2' variant='contrast'>
          <FansUnitedToggleTitle title={labels?.fu_play_free} />
        </SMPText>
        <SMPArrowUpIcon
          style={{
            width: '12px',
            height: '8px',
            transition: 'all 0.4s ease-out',
            transform: `rotate(${isExpanded ? '0deg' : '180deg'})`,
          }}
          css={{
            '&:hover': {
              color: '#FF901A',
              cursor: 'pointer',
            },
          }}
        />
      </SMPFlex>
      <SMPFlex variant='fansUnitedContentWrapper' display={isExpanded ? 'flex' : 'none'}>
        <SMPText size='2' weight='700' variant='contrast'>
          {labels?.fu_win_question ?? 'Who will win?'}
        </SMPText>
        <SMPFlex variant='fansUnitedOutcome1X2'>
          <SMPFlex variant='column' align='center' css={{ width: '60%', '@bp1': { width: 'auto' } }}>
            <SMPFlex
              variant='fansUnited1X2Pill'
              gap='sm'
              style={{ border: `${predictionSign === ONE ? '1px solid #FF901A' : 'none'}` }}
              onClick={() => handlePredictionClick(ONE)}
            >
              <SMPText variant='fansUnited1X2Label'>{homeTeam}</SMPText>
            </SMPFlex>

            <SMPFlex variant='column' align='center' css={{ mt: '11px', width: '100%' }} display={summary ? 'flex' : 'none'}>
              <SMPProgressBar value={summary?.['1']} css={{ width: '100%', '@bp1': { width: '115px' }, mb: '$2' }} />
              <SMPText size='2' variant='contrast'>
                {summary?.['1']}%
              </SMPText>
            </SMPFlex>
          </SMPFlex>

          <SMPFlex variant='column' align='center' css={{ width: '60%', '@bp1': { width: 'auto' } }}>
            <SMPFlex
              variant='fansUnited1X2Pill'
              style={{ border: `${predictionSign === X ? '1px solid #FF901A' : 'none'}` }}
              onClick={() => handlePredictionClick(X)}
            >
              <SMPText variant='fansUnited1X2Label'>{labels?.fu_draw ?? 'Draw'}</SMPText>
            </SMPFlex>

            <SMPFlex variant='column' align='center' css={{ mt: '11px', width: '100%' }} display={summary ? 'flex' : 'none'}>
              <SMPProgressBar value={summary?.x} css={{ width: '100%', '@bp1': { width: '115px' }, mb: '$2' }} />
              <SMPText size='2' variant='contrast'>
                {summary?.x}%
              </SMPText>
            </SMPFlex>
          </SMPFlex>

          <SMPFlex variant='column' align='center' css={{ width: '60%', '@bp1': { width: 'auto' } }}>
            <SMPFlex
              variant='fansUnited1X2Pill'
              style={{ border: `${predictionSign === TWO ? '1px solid #FF901A' : 'none'}` }}
              onClick={() => handlePredictionClick(TWO)}
            >
              <SMPText variant='fansUnited1X2Label'>{awayTeam}</SMPText>
            </SMPFlex>

            <SMPFlex variant='column' align='center' css={{ mt: '11px', width: '100%' }} display={summary ? 'flex' : 'none'}>
              <SMPProgressBar value={summary?.['2']} css={{ width: '100%', '@bp1': { width: '115px' }, mb: '$2' }} />
              <SMPText size='2' variant='contrast'>
                {summary?.['2']}%
              </SMPText>
            </SMPFlex>
          </SMPFlex>
        </SMPFlex>
      </SMPFlex>
    </Wrapper>
  );
};
